import db from './index.js'

export async function addMessages(messages) {
    await db.messages.bulkAdd(messages)
}

export async function setMessages(messages) {
    await db.messages.clear()
    await db.messages.bulkAdd(messages)
}

export async function putMessages(messages) {
    await db.messages.bulkPut(messages)
}

export async function getAllMessages() {
    return await db.messages.toArray()
}

export async function getMostRecentMessage() {
    return await db.messages.orderBy('createdAt').last()
}

export async function getMessagesForConversation(
    conversationId,
    limit = 50,
    beforeTimestamp = null
) {
    try {
        let query
        if (beforeTimestamp) {
            query = db.messages
                .where('[conversationId+createdAt]')
                .below([conversationId, beforeTimestamp])
                .reverse()
        } else {
            query = db.messages.where('conversationId').equals(conversationId)
            query = query.reverse()
        }
        const potentialMessages = await query.limit(limit + 1).toArray()
        const hasMoreMessages = potentialMessages.length > limit
        const messages = potentialMessages.slice(0, limit)
        messages.reverse()
        return { messages, hasMoreMessages: hasMoreMessages }
    } catch (error) {
        console.error(
            `Error retrieving messages for conversation ${conversationId} from IndexedDB:`,
            error
        )
        return { messages: [], hasMoreMessages: false }
    }
}

export async function getOlderMessages(
    conversationId,
    lastMessageCreatedAt,
    limit = 50
) {
    return await db.messages
        .where('conversationId')
        .equals(conversationId)
        .and('createdAt')
        .above(lastMessageCreatedAt)
        .limit(limit)
        .toArray()
}

export async function markMessages(messageIds, userToMark, options = {}) {
    const { read = false, received = false } = options
    await db.messages
        .where('_id')
        .anyOf(messageIds)
        .modify((message) => {
            if (read && !message.readBy?.includes(userToMark)) {
                message.readBy = [...(message.readBy || []), userToMark]
            }
            if (received && !message.receivedBy?.includes(userToMark)) {
                message.receivedBy = [...(message.receivedBy || []), userToMark]
            }
        })
}

export async function updateMessageModerationStatus(
    messageId,
    moderationStatus
) {
    await db.messages.where('_id').equals(messageId).modify({
        moderationStatus,
    })
}

export async function updateMessageTranscript(
    messageId,
    transcript,
    transcriptLanguage,
    transcriptionStatus,
    translations = null
) {
    await db.messages.where('_id').equals(messageId).modify((message) => {
        message.transcript = transcript
        message.transcriptLanguage = transcriptLanguage
        message.transcriptionStatus = transcriptionStatus
        // Merge translations if provided
        if (translations && Object.keys(translations).length > 0) {
            if (!message.translations) {
                message.translations = {}
            }
            // Merge new translations with existing ones
            Object.assign(message.translations, translations)
        }
    })
}

export async function updateMessageTranscriptsInDB(transcripts) {
    if (!transcripts || transcripts.length === 0) return

    const messages = await db.messages
        .where('_id')
        .anyOf(transcripts.map((t) => t.messageId))
        .toArray()

    const transcriptsMap = new Map(transcripts.map((t) => [t.messageId, t]))

    messages.forEach((message) => {
        const updateData = transcriptsMap.get(message._id)
        if (updateData) {
            message.transcript = updateData.transcript
            message.transcriptLanguage = updateData.transcriptLanguage
            message.transcriptionStatus = updateData.transcriptionStatus
            if (
                updateData.translations &&
                Object.keys(updateData.translations).length > 0
            ) {
                if (!message.translations) message.translations = {}
                Object.assign(message.translations, updateData.translations)
            }
        }
    })

    if (messages.length > 0) {
        await db.messages.bulkPut(messages)
    }
}

export async function updateOrderInMessage(order) {
    // Search for message that contains order._id and update the order in the message
    const message = await db.messages
        .where('order._id')
        .equals(order._id)
        .first()
    if (message) {
        message.order = order
        await db.messages.put(message)
    }
}

// Receive a list of mediaObjectIds and update the messages that contain them
export async function updateMediaObjectRefsInMessages(mediaObjects) {
    // Create a map of media objects by their ID for quick lookup
    const mediaObjectsMap = new Map(mediaObjects.map((m) => [m._id, m]))

    const mediaObjectIds = Array.from(mediaObjectsMap.keys())

    // Find all messages that reference any of the given media object IDs
    const messages = await db.messages
        .where('mediaObjectRef._id')
        .anyOf(mediaObjectIds)
        .toArray()

    // Update only those messages
    messages.forEach((message) => {
        const mediaObjectId = message.mediaObjectRef?._id
        if (mediaObjectId && mediaObjectsMap.has(mediaObjectId)) {
            message.mediaObjectRef = mediaObjectsMap.get(mediaObjectId)
        }
    })
    if (messages.length > 0) {
        await db.messages.bulkPut(messages)
    }
}

// Remove mediaObjectRef from all messages that reference a given media object id
export async function removeMediaObjectFromMessagesById(mediaObjectId) {
    try {
        await db.messages
            .where('mediaObjectRef._id')
            .equals(mediaObjectId)
            .modify((message) => {
                message.mediaObjectRef = null
            })
    } catch (error) {
        console.error(
            `Error removing mediaObjectRef for media ${mediaObjectId} in IndexedDB messages:`,
            error
        )
    }
}
